import React from 'react';
import { last } from 'lodash';
import {
  GetCoachingHomeDashboard_coaching_coachingHomeDashboard_participants_participantMetadata,
  GetCoachingHomeDashboard_coaching_coachingHomeDashboard_participants_coachingSessionSchedules,
  GetCoachingHomeDashboard_coaching_coachingHomeDashboard_participants_upcomingCoachingSessionInstances,
  GetCoachingHomeDashboard_coaching_coachingHomeDashboard_participants_sessionTools,
  GetCoachingHomeDashboard_coaching_coachingHomeDashboard_participants_previousCoaches,
} from 'coaching/queries/__generated__/GetCoachingHomeDashboard';
import { getFilteredListOfUpcomingInstances } from 'coaching/lib/coachingSessionInstances';
import CoachingNoUpcomingSessionsCard from 'coaching/components/CoachingHomeView/CoachingNoUpcomingSessionsCard';
import CoachingSessionsBySubjectBox from 'coaching/components/CoachingHomeView/CoachingSessionsBySubjectBox';
import CoachingSessionUpcomingInstanceRows from 'coaching/components/CoachingHomeView/CoachingSessionUpcomingInstanceRows';
import { OrganizationTutoringMetadata } from 'utils/coachingOrganizationHelpers';

import 'coaching/components/styles/CoachingSessionsBySubjectContainer.scss';
import { SelfScheduleOptions } from 'coaching/components/CoachingHomeView/BookNewCoachingSessionSidebar';

export type CoachMetadata = GetCoachingHomeDashboard_coaching_coachingHomeDashboard_participants_previousCoaches;
export type ParticipantMetadata = GetCoachingHomeDashboard_coaching_coachingHomeDashboard_participants_participantMetadata;
export type CoachingSessionSchedule = GetCoachingHomeDashboard_coaching_coachingHomeDashboard_participants_coachingSessionSchedules;
export type CoachingSessionInstance = GetCoachingHomeDashboard_coaching_coachingHomeDashboard_participants_upcomingCoachingSessionInstances;
export type CoachingSessionTools = GetCoachingHomeDashboard_coaching_coachingHomeDashboard_participants_sessionTools;

export type Props = {
  subjectName?: string;
  recurringSessionSchedules?: CoachingSessionSchedule[];
  participantMetadata: ParticipantMetadata;
  previousCoaches: CoachMetadata[];
  upcomingInstances?: CoachingSessionInstance[];
  sessionTools?: CoachingSessionTools;
  hasStudentInvitation: boolean;
  organizationTutoringMetadata: OrganizationTutoringMetadata;
  userUuid: string;
  selfScheduleOptions: SelfScheduleOptions;
};

export function getDefaultCoach(
  recurringSessionSchedules: CoachingSessionSchedule[] | undefined,
  previousCoaches: CoachMetadata[],
  upcomingInstances: CoachingSessionInstance[] | undefined
) {
  const coachName = upcomingInstances?.[0]?.coachName;
  const nextUpcomingInstanceCoach = previousCoaches?.find(coach => coach.name === coachName);

  return (
    recurringSessionSchedules?.[0]?.coach ?? nextUpcomingInstanceCoach ?? last(previousCoaches)
  );
}

export default function CoachingSessionsBySubjectContainer(props: Props) {
  const defaultCoach = getDefaultCoach(
    props.recurringSessionSchedules,
    props.previousCoaches,
    props.upcomingInstances
  );
  const emptyState = (
    <CoachingNoUpcomingSessionsCard
      participantUuid={props.participantMetadata.uuid}
      sessionTools={props.sessionTools}
      subjectName={props.subjectName}
      defaultCoach={defaultCoach}
      selfScheduleOptions={props.selfScheduleOptions}
      data-test="empty-state-card"
      organizationTutoring={props.organizationTutoringMetadata}
    />
  );
  if (!props.subjectName) {
    // if we don't have a subjectName, just show an empty box
    return (
      <CoachingSessionsBySubjectBox
        organizationTutoringMetadata={props.organizationTutoringMetadata}
        defaultCoach={defaultCoach}
        participantUuid={props.participantMetadata.uuid}
        selfScheduleOptions={props.selfScheduleOptions}>
        {emptyState}
      </CoachingSessionsBySubjectBox>
    );
  }

  const filteredAndOrderedInstances = getFilteredListOfUpcomingInstances(props.upcomingInstances);
  const sharedProps = {
    subjectName: props.subjectName,
    sessionTools: props.sessionTools,
    defaultCoach: defaultCoach,
    organizationTutoringMetadata: props.organizationTutoringMetadata,
  };

  return (
    <CoachingSessionsBySubjectBox
      {...sharedProps}
      organizationTutoringMetadata={props.organizationTutoringMetadata}
      recurringSessionSchedules={props.recurringSessionSchedules}
      upcomingInstances={filteredAndOrderedInstances}
      participantUuid={props.participantMetadata.uuid}
      selfScheduleOptions={props.selfScheduleOptions}>
      {filteredAndOrderedInstances && filteredAndOrderedInstances.length > 0 ? (
        <CoachingSessionUpcomingInstanceRows
          {...sharedProps}
          data-test="upcoming-instance-rows"
          instances={filteredAndOrderedInstances}
          recurringScheduleUuids={props.recurringSessionSchedules?.map(s => s.uuid) ?? []}
          userUuid={props.userUuid}
          participantMetadata={props.participantMetadata}
          hasStudentInvitation={props.hasStudentInvitation}
          selfScheduleOptions={props.selfScheduleOptions}
        />
      ) : (
        emptyState
      )}
    </CoachingSessionsBySubjectBox>
  );
}
